import { Button } from '@framework/ui/atoms/Button';
import { useCurrentUserEntity } from '@user/UserEntity';
import { useCallback, useState } from 'react';
import { UserSurveyOperation } from './UserSurveyOperation';
import { useGroupEntity } from '@group/hooks';

const GOOGLE_FORM_INFO = {
  ACTION_URL:
    'https://docs.google.com/forms/u/0/d/e/1FAIpQLScWx8vX0yu2zR2HIggSiFaH5vGJGDD6imt8EnQa66J2PtcjFQ/formResponse',
  SURVEY_ID_ID: '1378400464',
  GROUP_ID_ID: '693742132',
  GROUP_NAME_ID: '2062509255',
  USER_ID_ID: '337104524',
  USER_NAME_ID: '1582786260',
  EMAIL_ADDRESS_ID: '1824839066',
  POSITION_ID: '816142724',
  INDUSTRY_ID: '186008090',
  SIZE_OF_ORG_ID: '1049683192',
  PURPOSE_OF_USE_ID: '1376855717',
  EXPECTATION_ON_BALUS_ID: '711912707',
};

const FIELD_INFO: { [key: string]: { name: string; label: string } } = {
  surveyId: {
    name: `entry.${GOOGLE_FORM_INFO.SURVEY_ID_ID}`,
    label: 'アンケートID',
  },
  groupId: {
    name: `entry.${GOOGLE_FORM_INFO.GROUP_ID_ID}`,
    label: 'グループID',
  },
  groupName: {
    name: `entry.${GOOGLE_FORM_INFO.GROUP_NAME_ID}`,
    label: 'グループ名',
  },
  userId: {
    name: `entry.${GOOGLE_FORM_INFO.USER_ID_ID}`,
    label: 'ユーザID',
  },
  userName: {
    name: `entry.${GOOGLE_FORM_INFO.USER_NAME_ID}`,
    label: 'ユーザ名',
  },
  emailAddress: {
    name: `entry.${GOOGLE_FORM_INFO.EMAIL_ADDRESS_ID}`,
    label: 'メールアドレス',
  },
  position: {
    name: `entry.${GOOGLE_FORM_INFO.POSITION_ID}`,
    label: '現在の役職',
  },
  industry: {
    name: `entry.${GOOGLE_FORM_INFO.INDUSTRY_ID}`,
    label: 'ご所属の業界',
  },
  sizeOfOrg: {
    name: `entry.${GOOGLE_FORM_INFO.SIZE_OF_ORG_ID}`,
    label: '組織の規模',
  },
  purposeOfUse: {
    name: `entry.${GOOGLE_FORM_INFO.PURPOSE_OF_USE_ID}`,
    label: 'Balusのご利用目的（複数選択可）',
  },
  expectationOnBalus: {
    name: `entry.${GOOGLE_FORM_INFO.EXPECTATION_ON_BALUS_ID}`,
    label: 'Balusによってどのような成果・変化を期待しますか？（複数選択可）',
  },
};

const POSITION_DICT = {
  blank: '',
  owner: '経営層（経営者・役員）',
  manager: '管理職（部門長・マネージャーなど）',
  generalist: '一般職（担当者・スタッフ）',
  specialist: '営業・販売・マーケティング技術職（エンジニア・IT関連など）',
  designer: '企画・クリエイティブ（デザイン・プランニングなど）',
  admin: '管理部門（人事・総務・労務・法務など）',
  accounting: '経営企画・財務・経理',
  cs: '顧客対応（カスタマーサポート・カスタマーサクセスなど）',
  freelancer: '自営業・フリーランス',
  others: 'その他',
};

const INDUSTRY_DICT = {
  blank: '',
  it: 'IT・情報通信',
  media: '広告・出版・メディア',
  financial: '金融・保険',
  manufacturing: '製造・ものづくり',
  wholesale: '卸売・小売・流通',
  educational: '教育・学習支援',
  medical: '医療・福祉',
  construction: '建設・不動産',
  service: '飲食・宿泊・サービス業',
  public: '公共・官公庁・団体',
  entertainment: 'エンターテインメント・クリエイティブ',
  freelancer: '個人事業主・フリーランス',
  others: 'その他',
};

const SIZE_OF_ORG_DICT = {
  blank: '',
  size1: '1人（個人・フリーランス）',
  size2_10: '2〜10人',
  size11_50: '11〜50人',
  size51_200: '51〜200人',
  size201_500: '201〜500人',
  size501_1000: '501〜1,000人',
  sizeMoreThan1000: '1,001人以上',
  unknown: 'わからない／回答しない',
};

const PURPOSE_OF_USE_DICT = {
  taskManagement: 'タスク・プロジェクトの整理',
  visualizeIdea: 'アイデア出し・発想の可視化',
  sharing: 'チーム内の情報共有・ドキュメント管理',
  knowledgeManagement: '知識の整理・ナレッジ管理',
  illustrate: '図解・マップ作成（フロー図・マインドマップなど）',
  collaboration: '社内外とのコラボレーション',
  meeting: '会議・ワークショップの進行・可視化',
  presentation: '資料・プレゼンテーションの作成',
  reflection: 'チームコミュニケーションの活性化（ふりかえりなど）',
  otherPurpose: 'その他（自由記述）',
};

const EXPECTATION_ON_BALUS_DICT = {
  handleComplexity: '複雑な情報や状況を整理したい',
  understandCustomer: '顧客理解を深めたい',
  diveIntoNewRealm: '新しい領域・テーマを理解したい',
  identifyIssue: '問題・課題を特定したい',
  goalSetting: '意義のある目標を設定したい',
  planning: 'チームやプロジェクトの方針を立てたい',
  evolveProduct: 'プロダクトやサービスを進化させたい',
  reconsiderBusinessModel: '自社ビジネスを見直したい／変革したい',
  organizationReform: '組織のあり方を変えたい',
  deepenThought: '一人で思考を整理したい',
  accelerateTeamwork: 'チームや組織に合ったツールを選定したい',
  otherExpectation: 'その他（自由記述）',
};

type Props = {
  groupId: string;
  userId: string;
  surveyId: string;
  onAnswerCompleted(): void;
  onCancel(): void;
};

export const UserSurveyForm: React.FC<Props> = ({ groupId, userId, surveyId, onAnswerCompleted, onCancel }: Props) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [position, setPosition] = useState<string>('');
  const [industry, setIndustry] = useState<string>('');
  const [sizeOfOrg, setSizeOfOrg] = useState<string>('');
  const [purposeOfUse, setPurposeOfUse] = useState<string>('');
  const [expectationOnBalus, setExpectationOnBalus] = useState<string>('');

  const [purposeOfUseChecked, setPurposeOfUseChecked] = useState<boolean[]>(
    new Array(Object.keys(PURPOSE_OF_USE_DICT).length).fill(false)
  );
  const [expectationOnBalusChecked, setExpectationOnBalusChecked] = useState<boolean[]>(
    new Array(Object.keys(EXPECTATION_ON_BALUS_DICT).length).fill(false)
  );

  const [purposeOfUseOthers, setPurposeOfUseOthers] = useState<string>('');
  const [expectationOnBalusOthers, setExpectationOnBalusOthers] = useState<string>('');

  const userEntity = useCurrentUserEntity();
  const { group } = useGroupEntity(groupId);

  const handleChangePosition = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setPosition(event.target.value);
  }, []);

  const handleChangeIndustry = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setIndustry(event.target.value);
  }, []);

  const handleChangeSizeOfOrg = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSizeOfOrg(event.target.value);
  }, []);

  const mergePurposeOfUseValues = (status: boolean[], othersValue: string) => {
    const merged = Object.values(PURPOSE_OF_USE_DICT)
      .filter((_, index) => status[index])
      .map((value) => {
        if (value === PURPOSE_OF_USE_DICT.otherPurpose) {
          return `${value}: ${othersValue}`;
        } else {
          return value;
        }
      })
      .join('\n');

    return merged;
  };

  // 利用目的チェックボックス
  const handleChangePurposeOfUse = useCallback(
    (changeIndex: number) => {
      const changedStatus = purposeOfUseChecked.map((checked, index) => {
        return index === changeIndex ? !checked : checked;
      });
      setPurposeOfUseChecked(changedStatus);
      setPurposeOfUse(mergePurposeOfUseValues(changedStatus, purposeOfUseOthers));
    },
    [purposeOfUseChecked, purposeOfUseOthers]
  );

  // 利用目的「その他（自由記述）」テキストボックス
  const handleChangePurposeOfUseOthers = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const othersValue = event.target.value;

      setPurposeOfUseOthers(othersValue);
      setPurposeOfUse(mergePurposeOfUseValues(purposeOfUseChecked, othersValue));
    },
    [purposeOfUseChecked]
  );

  const mergeExpectationOnBalusValues = (status: boolean[], othersValue: string) => {
    const merged = Object.values(EXPECTATION_ON_BALUS_DICT)
      .filter((_, index) => status[index])
      .map((value) => {
        if (value === EXPECTATION_ON_BALUS_DICT.otherExpectation) {
          return `${value}: ${othersValue}`;
        } else {
          return value;
        }
      })
      .join('\n');

    return merged;
  };

  // Balusから得られることへの期待チェックボックス
  const handleChangeExpectationOnBalus = useCallback(
    (changeIndex: number) => {
      const changedStatus = expectationOnBalusChecked.map((checked, index) => {
        return index === changeIndex ? !checked : checked;
      });
      setExpectationOnBalusChecked(changedStatus);
      setExpectationOnBalus(mergeExpectationOnBalusValues(changedStatus, expectationOnBalusOthers));
    },
    [expectationOnBalusChecked, expectationOnBalusOthers]
  );

  // Balusから得られることへの期待「その他（自由記述）」テキストボックス
  const handleChangeExpectationOnBalusOthers = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const othersValue = event.target.value;

      setExpectationOnBalusOthers(othersValue);
      setExpectationOnBalus(mergeExpectationOnBalusValues(expectationOnBalusChecked, othersValue));
    },
    [expectationOnBalusChecked]
  );

  const handleSubmit = useCallback(async () => {
    const status = await UserSurveyOperation.answer(userId);
    if (status === 'ReadyToAnswer') {
      setIsSubmitted(true);

      return true;
    } else {
      return false;
    }
  }, [userId]);

  const isInputCompleted = useCallback(() => {
    return !!position && !!industry && !!sizeOfOrg && !!purposeOfUse && !!expectationOnBalus;
  }, [position, industry, sizeOfOrg, purposeOfUse, expectationOnBalus]);

  const onIframeLoaded = useCallback(() => {
    if (isSubmitted) {
      onAnswerCompleted();
    }
  }, [isSubmitted, onAnswerCompleted]);

  const createPurposeOfUseCheckbox = (value: string, label: string, index: number) => (
    <label className="mb-2 block w-[440px]" htmlFor={value} key={value}>
      <input
        id={value}
        type="checkbox"
        key={value}
        value={label}
        className={'form-checkbox mr-4'}
        checked={purposeOfUseChecked[index]}
        onChange={() => handleChangePurposeOfUse(index)}
      />
      {label}
    </label>
  );

  const createExpectationOnBalusCheckbox = (value: string, label: string, index: number) => (
    <label className="mb-2 block w-[340px]" htmlFor={value} key={value}>
      <input
        id={value}
        type="checkbox"
        key={value}
        value={label}
        className={'form-checkbox mr-4'}
        checked={expectationOnBalusChecked[index]}
        onChange={() => handleChangeExpectationOnBalus(index)}
      />
      {label}
    </label>
  );

  return (
    <form action={GOOGLE_FORM_INFO.ACTION_URL} method="POST" target="thanks_iframe" onSubmit={handleSubmit}>
      <iframe name="thanks_iframe" style={{ display: 'none' }} onLoad={onIframeLoaded} />

      <input type="hidden" name={FIELD_INFO.surveyId.name} value={surveyId} />
      <input type="hidden" name={FIELD_INFO.groupId.name} value={groupId} />

      {group && <input type="hidden" name={FIELD_INFO.groupName.name} value={group.name.toString()} />}
      <input type="hidden" name={FIELD_INFO.userId.name} value={userId} />

      {userEntity && (
        <>
          <input type="hidden" name={FIELD_INFO.userName.name} value={userEntity.name} />
          <input type="hidden" name={FIELD_INFO.emailAddress.name} value={userEntity.mailAddress} />
        </>
      )}

      <input type="hidden" name={FIELD_INFO.purposeOfUse.name} value={purposeOfUse} />
      <input type="hidden" name={FIELD_INFO.expectationOnBalus.name} value={expectationOnBalus} />

      <div style={{ width: '1280px', height: '520px' }}>
        <div className="flex flex-col px-8 pb-8">
          <div className="flex flex-row">
            <div>
              <div className="w-4/5 py-2 font-bold">{FIELD_INFO.position.label}</div>
              <div>
                <select
                  name={FIELD_INFO.position.name}
                  onChange={handleChangePosition}
                  value={position}
                  className="borderfocus:outline-none form-select border-brand"
                >
                  {Object.entries(POSITION_DICT).map(([value, label]) => (
                    <option key={value} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pl-14">
              <div className="w-4/5 py-2 font-bold">{FIELD_INFO.industry.label}</div>
              <div>
                <select
                  name={FIELD_INFO.industry.name}
                  onChange={handleChangeIndustry}
                  value={industry}
                  className="borderfocus:outline-none form-select border-brand"
                >
                  {Object.entries(INDUSTRY_DICT).map(([value, label]) => (
                    <option key={value} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pl-10">
              <div className="w-4/5 py-2 font-bold">{FIELD_INFO.sizeOfOrg.label}</div>
              <div>
                <select
                  name={FIELD_INFO.sizeOfOrg.name}
                  onChange={handleChangeSizeOfOrg}
                  value={sizeOfOrg}
                  className="borderfocus:outline-none form-select border-brand"
                >
                  {Object.entries(SIZE_OF_ORG_DICT).map(([value, label]) => (
                    <option key={value} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-row">
            <div>
              <div className="pb-4 pt-6 font-bold">{FIELD_INFO.purposeOfUse.label}</div>
              <div>
                {Object.entries(PURPOSE_OF_USE_DICT).map(([value, label], index) =>
                  createPurposeOfUseCheckbox(value, label, index)
                )}
                <input
                  type="text"
                  className="w-[518px] rounded-sm border p-2"
                  value={purposeOfUseOthers}
                  onChange={handleChangePurposeOfUseOthers}
                />
              </div>
            </div>
            <div className="pl-14">
              <div className="pb-4 pt-6 font-bold">{FIELD_INFO.expectationOnBalus.label}</div>
              <div>
                {
                  Object.entries(EXPECTATION_ON_BALUS_DICT).reduce(
                    ([prev, total], [value, label], index) => {
                      if (index <= 3) {
                        // 最初の２段はチェックボックスを横並びにする
                        if (index % 2 === 0) {
                          return [createExpectationOnBalusCheckbox(value, label, index), total];
                        } else {
                          return [
                            <></>,
                            <>
                              {total}
                              <div key={`div_${index}`} className="flex flex-row">
                                {prev}
                                {createExpectationOnBalusCheckbox(value, label, index)}
                              </div>
                            </>,
                          ];
                        }
                      } else {
                        return [
                          <></>,
                          <>
                            {total}
                            {createExpectationOnBalusCheckbox(value, label, index)}
                          </>,
                        ];
                      }
                    },
                    [<></>, <></>]
                  )[1]
                }
                <input
                  type="text"
                  className="w-[518px] rounded-sm border p-2"
                  value={expectationOnBalusOthers}
                  onChange={handleChangeExpectationOnBalusOthers}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={'flex justify-end py-2'}>
        <div className={'pr-2'}>
          <Button type="submit" color="primary" disabled={!isInputCompleted()} loading={isSubmitted}>
            この内容で送信する
          </Button>
        </div>
        <div className={'pr-2'}>
          <Button type="button" color="secondary" onClick={onCancel}>
            閉じる
          </Button>
        </div>
      </div>
    </form>
  );
};
